/**
 *
 * IBM Confidential
 *
 * (C) Copyright IBM Corp. 2021, 2025
 *
 * The source code for this program is not published or otherwise
 * divested of its trade secrets, irrespective of what has been
 * deposited with the U. S. Copyright Office
 *
 * US Government Users Restricted Rights - Use, duplication or
 * disclosure restricted by GSA ADP Schedule Contract with IBM Corp.
 *
 **/

import urlJoin from 'proper-url-join';

import { getServerBaseUri } from '../../../ia/util/url';

// NOTE that the "const" keyword isn't used here,
// because const enum values are erased at compile time.
export enum AUTHENTICATION_METHODS {
  BASIC = 'basic',
  BASIC_SCHEME = 'basic',
  BEARER = 'bearer',
  BEARER_SCHEME = 'bearer',
  API_KEY = 'api_key',
  API_KEY_TYPE = 'apiKey',
  OAUTH2 = 'oauth2',
  NONE = 'NoAuth'
}

export enum RESPONSE_METHODS {
  GET = 'GET',
  POST = 'POST',
  PUT = 'PUT',
  DELETE = 'DELETE',
  PATCH = 'PATCH',
  HEAD = 'HEAD',
  OPTIONS = 'OPTIONS',
  TRACE = 'TRACE'
}

export enum OAUTH2_FLOW_TYPES {
  IMPLICIT = 'implicit',
  PASSWORD = 'password',
  CLIENT_CREDENTIALS = 'client_credentials',
  AUTHORIZATION_CODE = 'authorization_code',
  CUSTOM = 'custom'
}

export const VALID_RESPONSE_METHODS = [
  RESPONSE_METHODS.GET,
  RESPONSE_METHODS.POST,
  RESPONSE_METHODS.PUT,
  RESPONSE_METHODS.DELETE,
  RESPONSE_METHODS.PATCH
];

export const SUPPORTED_OAUTH2_FLOWS = [
  OAUTH2_FLOW_TYPES.CLIENT_CREDENTIALS,
  OAUTH2_FLOW_TYPES.PASSWORD,
  OAUTH2_FLOW_TYPES.AUTHORIZATION_CODE
];

export const ARRAY = 'array';
export const OBJECT = 'object';
export const CATALOG_ITEM_TYPE = 'OpenAPI';

export const MEDIA_APPLICATION_JSON = 'application/json';
export const MEDIA_EVENT_STREAM = 'text/event-stream';

export const DEFAULT_RESPONSE_PARAMETERS = status => [
  {
    key: 'success',
    value: status >= 200 && status < 300
  },
  {
    key: 'status',
    value: status
  }
];

export const EXTENSION_DESCRIPTION_MAX_CHAR_COUNT = 128;
export const TOAST_NOTIFICATION_TIMEOUT_SUCCESS_OAUTH_CODE = 10000;

// OAuth2 secret fields begin
export const CLIENT_ID = 'client_id';
export const CLIENT_SECRET = 'client_secret';
export const ACCEES_TOKEN = 'access_token';
export const REFRESH_TOKEN = 'refresh_token';
export const AUTHORIZATION_CODE = 'authorization_code';
export const USERNAME = 'username';
export const PASSWORD = 'password';
// OAuth2 secret fields end

export const BODY = 'Body';
export const BASIC_AUTH_HEADER = 'BasicAuthHeader';
export const BEARER = 'Bearer';
export const CODE = 'code';
export const CONTENT_APPLICATION_FORM_URLENCODED =
  'application/x-www-form-urlencoded';
export const HEADER = 'header';
export const QUERY = 'query';
export const STATE = 'state';
export const VALUE = 'value';

export const createDefaultAccessRefreshTokenObject = () => {
  return { expires_by: '', lifespan: '', type: VALUE, value: '' };
};

export const OAUTH2_FLOWS_REQUIRED_FIELDS = {
  [OAUTH2_FLOW_TYPES.PASSWORD]: [CLIENT_ID, CLIENT_SECRET, USERNAME, PASSWORD],
  [OAUTH2_FLOW_TYPES.CLIENT_CREDENTIALS]: [CLIENT_ID, CLIENT_SECRET],
  [OAUTH2_FLOW_TYPES.AUTHORIZATION_CODE]: [CLIENT_ID, CLIENT_SECRET]
};

// In WXO, the user should use as the redirection URL the base address of
// the WXO server plus the MFE prefix for Assistant, so that the Tooling
// server gets the request.
export const OAUTH2_AUTHORIZATION_FLOW_REDIRECT_URI = () =>
  urlJoin(getServerBaseUri(), 'extension/oauth');

export const DEFAULT_OAUTH_FLOW_SCHEMA = {
  tokenUrl: '',
  refreshUrl: '',
  scopes: {},
  authorizationUrl: ''
};

// OAuth2 custom flow (which starts with x-) non secret default schema
export const defaultOAuth2CustomFlowSchema = () => {
  return {
    client_auth_type: '',
    content_type: CONTENT_APPLICATION_FORM_URLENCODED,
    grant_type: '',
    header_prefix: '',
    params: {},
    refresh_url: '',
    token_url: '',
    scopes: {}
  };
};

// OAuth2 custom flow (which starts with x-) secret default schema
export const defaultOAuth2CustomFlowSecretSchema = () => {
  return {
    access_token: createDefaultAccessRefreshTokenObject(),
    custom_secrets: {}
  };
};

// This creates a default object {type: 'value', value: ''}
const createIntegrationConfigKeyValue = () => {
  return {
    type: VALUE,
    value: ''
  };
};

// This creates a default OAuth2 Client credentials flow schema for edit mode
const defaultOAuth2AuthClientCredsSecretsSchema = () => {
  return {
    [CLIENT_ID]: createIntegrationConfigKeyValue(),
    [CLIENT_SECRET]: createIntegrationConfigKeyValue(),
    [ACCEES_TOKEN]: createDefaultAccessRefreshTokenObject(),
    [REFRESH_TOKEN]: createDefaultAccessRefreshTokenObject()
  };
};

// This creates a default OAuth2 Password flow schema for edit mode
const defaultOAuth2AuthPasswordSecretsSchema = () => {
  return {
    [CLIENT_ID]: createIntegrationConfigKeyValue(),
    [CLIENT_SECRET]: createIntegrationConfigKeyValue(),
    [PASSWORD]: createIntegrationConfigKeyValue(),
    [ACCEES_TOKEN]: createDefaultAccessRefreshTokenObject(),
    [REFRESH_TOKEN]: createDefaultAccessRefreshTokenObject()
  };
};

// This creates a default OAuth2 Authorization code flow schema for edit mode
const defaultOAuth2AuthCodeSecretsSchema = () => {
  return {
    [AUTHORIZATION_CODE]: '',
    [CLIENT_ID]: createIntegrationConfigKeyValue(),
    [CLIENT_SECRET]: createIntegrationConfigKeyValue(),
    [ACCEES_TOKEN]: createDefaultAccessRefreshTokenObject(),
    [REFRESH_TOKEN]: createDefaultAccessRefreshTokenObject()
  };
};

// FIXME if/when store would support partial secret updates
// For quicksetup & Edit flow, store needs to have an entire secrets object (UI required secret fields + non UI secret fields like access & refresh token)
// This will be mapped in AuthenticationContentOAuth.tsx to create a placeholder object of selected flow for edit flow (quickswtup flow is done with schema.tsx).
export const defaultOAuth2Secrets = () => {
  return {
    [OAUTH2_FLOW_TYPES.PASSWORD]: defaultOAuth2AuthPasswordSecretsSchema(),
    [OAUTH2_FLOW_TYPES.CLIENT_CREDENTIALS]:
      defaultOAuth2AuthClientCredsSecretsSchema(),
    [OAUTH2_FLOW_TYPES.AUTHORIZATION_CODE]: defaultOAuth2AuthCodeSecretsSchema()
  };
};
